import { PRODUCT_NAME } from "@/common/constants";
import { isSupportRequestOpen } from "@/common/relationships/src/utils";
import { RESERVED_ORG_IDS } from "@/state";
import type { CustomerDomainOrg } from "@/state/types";

import type {
    ForYouPageDetailsAnalyticsAttributes,
    SummaryData
} from "../types";
import type {
    GetFastshiftSupportRequestsResponseData,
    GetFastshiftCustomerDomainResponseData
} from "@wac-web/libraries/bxp-web-stargate-client/src";

export const isSupportedProduct = (productName?: string | null) => {
    const normalizedName = (productName ?? "").toUpperCase();
    return (
        normalizedName === PRODUCT_NAME.JIRA ||
        normalizedName === PRODUCT_NAME.CONFLUENCE
    );
};

export function buildForYouPageDetailsAttributes(args: {
    customerDomain: GetFastshiftCustomerDomainResponseData;
    domainOrg: CustomerDomainOrg;
    summaryData: SummaryData;
    summaryEmpty: boolean;
    supportRequests: GetFastshiftSupportRequestsResponseData;
}): ForYouPageDetailsAnalyticsAttributes {
    const {
        customerDomain,
        domainOrg,
        summaryData,
        summaryEmpty,
        supportRequests
    } = args;

    // orgs & support counts
    const selectedOrgId = domainOrg.orgId ?? RESERVED_ORG_IDS.NO_ORG;
    const orgCount = customerDomain.orgsWithAdminRole.length;
    const planTicketCount = supportRequests.length;
    const planTicketList = supportRequests.map((req) => req.supportTicketId);

    // summary section
    const summaryCardsDisplayed = !summaryEmpty;
    const noAssessmentsBannerDisplayed =
        selectedOrgId !== RESERVED_ORG_IDS.NO_ORG && summaryEmpty;
    const countAssessedInstances = summaryData.dataCenter.count ?? null;
    const countAreasToImprove = summaryData.areaImprovement.count ?? null;
    const countDetectedApps = summaryData.deletedApps.count ?? null;
    const countMigrationsCompleted =
        summaryData.migrationCompleted.count ?? null;
    const countMigrationsInProgress =
        summaryData.migrationInProgress.count ?? null;
    const countUnsuccessfulMigrations =
        summaryData.unsuccessfulMigration.count ?? null;

    // request fastshift assistance section
    const hasRequestAssistancePathway = supportRequests.some((req) =>
        isSupportRequestOpen(req.status)
    );

    // data Quality of Progress Table
    const rowsMissingProductIcon = supportRequests.filter((r) => {
        const sourceSupported = isSupportedProduct(r.source.productName);
        const destinationSupported = isSupportedProduct(
            r.destination.productName
        );
        return !(sourceSupported && destinationSupported);
    }).length;

    return {
        selectedOrgId,
        orgCount,
        planTicketCount,
        planTicketList,
        summaryCardsDisplayed,
        noAssessmentsBannerDisplayed,
        countAssessedInstances,
        countAreasToImprove,
        countDetectedApps,
        countMigrationsCompleted,
        countMigrationsInProgress,
        countUnsuccessfulMigrations,
        hasRequestAssistancePathway,
        rowsMissingProductIcon
    };
}
